import { Card } from '@/components/ui/card';
import { Target, Scissors, Package } from 'lucide-react';

/**
 * Per-branch monthly goal progress card (Metas por Sucursal).
 * Renders one sub-card per branch that has a target > 0.
 * Consumes the `by_branch` array from GET /api/zones/dashboard/sales.
 */
export default function BranchTargetsSection({ byBranch }) {
  const branchesWithTargets = (byBranch || []).filter(b => (b.targets?.total || 0) > 0);
  if (branchesWithTargets.length === 0) return null;

  const money = (n) => '$' + Number(n || 0).toLocaleString('es-MX', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  // Rank branches by total progress % desc (for position indicator)
  const ranked = [...branchesWithTargets].sort((a, b) => (b.progress?.total_pct || 0) - (a.progress?.total_pct || 0));

  const barColor = (pct) => {
    if (pct >= 100) return 'bg-green-500';
    if (pct >= 75) return 'bg-gold';
    if (pct >= 50) return 'bg-yellow-500';
    return 'bg-orange-500';
  };

  return (
    <Card className="bg-zinc-900/50 border-zinc-800 p-5 space-y-4" data-testid="branch-targets-section">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Target className="w-4 h-4 text-gold" />
          <span className="text-sm text-zinc-300 font-medium">Metas por Sucursal</span>
        </div>
        <span className="text-[10px] text-zinc-500">Progreso independiente por sucursal</span>
      </div>

      <div className={`grid gap-3 ${branchesWithTargets.length === 1 ? 'grid-cols-1' : branchesWithTargets.length === 2 ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'}`}>
        {ranked.map((b, idx) => {
          const t = b.targets || {};
          const p = b.progress || {};
          const m = b.missing || {};
          const pace = b.pace || {};
          const actualServices = b.month?.services_revenue || 0;
          const actualProducts = b.month?.products_revenue || 0;
          const actualTotal = actualServices + actualProducts;
          const ahead = pace.pace_status === 'ahead';

          return (
            <div
              key={b.branch_id}
              className="bg-zinc-950/60 border border-zinc-800 rounded-sm p-4 space-y-3"
              data-testid={`branch-target-card-${b.branch_id}`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold ${
                    idx === 0 ? 'bg-gold/20 text-gold' : idx === 1 ? 'bg-zinc-700 text-zinc-300' : 'bg-zinc-800 text-zinc-400'
                  }`}>
                    {idx + 1}
                  </div>
                  <p className="text-sm text-white font-medium truncate">{b.branch_name}</p>
                </div>
                <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                  p.total_pct >= 100 ? 'bg-green-500/20 text-green-400' :
                  ahead ? 'bg-blue-500/20 text-blue-400' : 'bg-red-500/20 text-red-400'
                }`}>
                  {p.total_pct >= 100 ? 'Meta ✓' : ahead ? 'Adelantado' : 'Atrasado'}
                </span>
              </div>

              <div>
                <div className="flex justify-between items-baseline mb-1">
                  <span className="text-[10px] text-zinc-500 uppercase tracking-wider">Total</span>
                  <span className="text-xs text-white font-semibold">
                    {money(actualTotal)} <span className="text-zinc-500">/ {money(t.total)}</span>
                  </span>
                </div>
                <div className="h-2 bg-zinc-800 rounded overflow-hidden">
                  <div
                    className={`h-full ${barColor(p.total_pct)} transition-all`}
                    style={{ width: `${Math.min(p.total_pct || 0, 100)}%` }}
                  />
                </div>
                <div className="flex justify-between mt-1">
                  <span className="text-[10px] text-zinc-500">{(p.total_pct || 0).toFixed(1)}%</span>
                  <span className="text-[10px] text-zinc-500">Faltan {money(m.total)}</span>
                </div>
              </div>

              <div>
                <div className="flex justify-between items-baseline mb-1">
                  <span className="text-[10px] text-green-400 uppercase tracking-wider flex items-center gap-1">
                    <Scissors className="w-3 h-3" /> Servicios
                  </span>
                  <span className="text-[11px] text-zinc-300">
                    {money(actualServices)} <span className="text-zinc-600">/ {money(t.services)}</span>
                  </span>
                </div>
                <div className="h-1.5 bg-zinc-800 rounded overflow-hidden">
                  <div
                    className="h-full bg-green-500 transition-all"
                    style={{ width: `${Math.min(p.services_pct || 0, 100)}%` }}
                  />
                </div>
                <p className="text-[10px] text-zinc-500 mt-0.5">
                  {(p.services_pct || 0).toFixed(1)}% · Faltan {money(m.services)}
                </p>
              </div>

              <div>
                <div className="flex justify-between items-baseline mb-1">
                  <span className="text-[10px] text-blue-400 uppercase tracking-wider flex items-center gap-1">
                    <Package className="w-3 h-3" /> Productos
                  </span>
                  <span className="text-[11px] text-zinc-300">
                    {money(actualProducts)} <span className="text-zinc-600">/ {money(t.products)}</span>
                  </span>
                </div>
                <div className="h-1.5 bg-zinc-800 rounded overflow-hidden">
                  <div
                    className="h-full bg-blue-500 transition-all"
                    style={{ width: `${Math.min(p.products_pct || 0, 100)}%` }}
                  />
                </div>
                <p className="text-[10px] text-zinc-500 mt-0.5">
                  {(p.products_pct || 0).toFixed(1)}% · Faltan {money(m.products)}
                </p>
              </div>

              {t.total > 0 && (
                <div className={`text-[10px] px-2 py-1 rounded border ${ahead ? 'bg-green-500/10 border-green-500/30 text-green-300' : 'bg-red-500/10 border-red-500/30 text-red-300'}`}>
                  {ahead ? '↑' : '↓'} {ahead ? 'Adelanto' : 'Atraso'}: {money(Math.abs(pace.pace_delta || 0))}
                  <span className="text-zinc-500 ml-1">
                    (esperado {money(pace.expected_today)})
                  </span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Card>
  );
}
